import { Button } from '@/components/ui/button'
import { ForumAdminBadge, ForumPinnedBadge } from '@/components/forum/forum-badges'
import {
  canPostToBoard,
  localizedBoardDescription,
  localizedBoardName,
  pickTranslation,
} from '@/components/forum/forum-i18n'
import { useAuth } from '@/lib/auth'
import {
  applyArenaDynamicHead,
  arenaLanguageTag,
  arenaSeoOrigin,
  arenaSeoRobots,
} from '@/lib/arena-dynamic-seo'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { formatDate } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { MessageSquare, Plus } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

const FORUM_TITLE = 'Werewolf Strategy Forum'
const FORUM_LEAD = 'The Mentiss forum is the discussion hub for players studying Werewolf decisions, AI model behavior, and social-deduction strategy. Open a board to read its current threads and join the conversation where posting is available.'

export function ForumPage() {
  const { t, language } = useI18n()
  const isEnglishLanding = language === 'en'
  const heading = isEnglishLanding ? FORUM_TITLE : t('forum.title')
  const lead = isEnglishLanding ? FORUM_LEAD : t('forum.subtitle')

  useLayoutEffect(() => {
    document.getElementById('seo-forum-prelude')?.remove()
    const origin = arenaSeoOrigin()
    const description = isEnglishLanding
      ? 'Discuss Werewolf strategy, analyze Mentiss game logs, share matches, and connect with players exploring human and AI social-deduction play.'
      : lead
    const canonical = isEnglishLanding ? `${origin}/en/forum` : undefined
    return applyArenaDynamicHead({
      title: `${heading} | Mentiss Arena`,
      socialTitle: heading,
      description,
      language: arenaLanguageTag(language),
      robots: arenaSeoRobots(isEnglishLanding),
      canonical,
      structuredDataId: isEnglishLanding ? 'seo-forum-structured-data' : undefined,
      structuredData: isEnglishLanding ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': heading,
        'headline': heading,
        description,
        'url': canonical,
        'inLanguage': 'en',
        'isPartOf': {
          '@type': 'WebSite',
          'name': 'Mentiss Werewolf Arena',
          'url': `${origin}/en`,
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Mentiss',
          'url': 'https://mentiss.ai/en',
        },
        'about': ['Werewolf strategy', 'AI game analysis', 'Social deduction community']
          .map(name => ({ '@type': 'Thing', name })),
      } : undefined,
    })
  }, [heading, isEnglishLanding, language, lead])

  const trpc = useTRPC()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: boardsData, isLoading: boardsLoading } = useQuery(
    trpc.forumRouter.listBoards.queryOptions(),
  )
  const boards = useMemo(() => boardsData?.boards ?? [], [boardsData])

  const boardSlug = searchParams.get('board') ?? boards[0]?.slug ?? ''
  const page = Math.max(1, Number(searchParams.get('page')) || 1)

  useEffect(() => {
    if (!searchParams.get('board') && boards[0]?.slug) {
      setSearchParams({ board: boards[0].slug }, { replace: true })
    }
  }, [boards, searchParams, setSearchParams])

  const { data: threadsData, isLoading: threadsLoading } = useQuery({
    ...trpc.forumRouter.listThreads.queryOptions({ boardSlug, page }),
    enabled: !!boardSlug,
  })

  const selectBoard = (slug: string) => {
    setSearchParams({ board: slug })
  }

  const goToPage = (next: number) => {
    setSearchParams({ board: boardSlug, page: String(next) })
  }

  const threads = threadsData?.threads ?? []
  const totalPages = threadsData?.totalPages ?? 1
  const activeBoard = boards.find(b => b.slug === boardSlug)

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-12 pt-24 md:px-6" lang={arenaLanguageTag(language)}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">{heading}</h1>
        <p className="mt-2 text-muted-foreground">{lead}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        { }
        <aside className="lg:w-60 lg:flex-shrink-0">
          <h2 className="mb-2 px-1 text-sm font-medium text-muted-foreground">
            {t('forum.boards')}
          </h2>
          <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
            {boardsLoading
              ? (
                  <div className="px-1 py-2 text-sm text-muted-foreground">{t('forum.loading')}</div>
                )
              : boards.map(b => (
                  <button
                    key={b.slug}
                    type="button"
                    aria-current={b.slug === boardSlug ? 'page' : undefined}
                    onClick={() => selectBoard(b.slug)}
                    className={`flex-shrink-0 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors lg:w-full ${
                      b.slug === boardSlug
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                  >
                    <span className="block whitespace-nowrap lg:whitespace-normal">{localizedBoardName(t, b)}</span>
                    <span className="hidden text-xs text-muted-foreground lg:block">
                      {t('forum.threadCount', { count: b.threadCount })}
                    </span>
                  </button>
                ))}
          </nav>
        </aside>

        { }
        <main className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold text-foreground">
                {activeBoard ? localizedBoardName(t, activeBoard) : ' '}
              </h2>
              {activeBoard?.description && (
                <p className="truncate text-sm text-muted-foreground">
                  {localizedBoardDescription(t, activeBoard)}
                </p>
              )}
            </div>
            {
            }
            {canPostToBoard(boardSlug, user?.email) && (
              <Button
                className="flex-shrink-0"
                onClick={() =>
                  navigate(
                    isAuthenticated
                      ? `/forum/new?board=${boardSlug}`
                      : `/signin?redirect=${encodeURIComponent(`/forum/new?board=${boardSlug}`)}`,
                  )}
              >
                <Plus className="size-4" />
                {t('forum.newThread')}
              </Button>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {threadsLoading
              ? (
                  <div className="px-4 py-12 text-center text-muted-foreground">
                    {t('forum.loading')}
                  </div>
                )
              : threads.length === 0
                ? (
                    <div className="px-4 py-12 text-center text-muted-foreground">
                      {t('forum.emptyThreads')}
                    </div>
                  )
                : (
                    <ul>
                      {threads.map(thread => (
                        <li key={thread.id} className="border-b border-border last:border-0">
                          <Link
                            to={`/forum/thread/${thread.id}`}
                            className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted/40"
                          >
                            <div className="min-w-0">
                              <div className="flex min-w-0 items-start gap-2">
                                <p className="line-clamp-2 font-medium text-foreground">
                                  {pickTranslation(thread.titleTranslations, language) ?? thread.title}
                                </p>
                                {thread.isPinned && <ForumPinnedBadge />}
                              </div>
                              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1 text-xs text-muted-foreground">
                                <span>
                                  {t('forum.by')}
                                  {' '}
                                  {thread.authorName}
                                </span>
                                {thread.isAdminAuthor && <ForumAdminBadge />}
                                <span>{' · '}{formatDate(thread.createdAt)}</span>
                              </div>
                            </div>
                            <span className="flex flex-shrink-0 items-center gap-1 text-sm text-muted-foreground">
                              <MessageSquare className="size-4" />
                              {thread.replyCount}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                {t('forum.prev')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('forum.page', { page, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >
                {t('forum.next')}
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
